import { useEffect, useState } from 'react';
import { compose, defaultTo, dissoc, dissocPath, evolve, map, modify, omit, pathEq, prop, startsWith, when } from 'ramda';
import { getZipCodeRules } from '@@viewHelpers/zipCode';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import { dateTimeToMidday, dateTsToSystem } from '@@viewHelpers/date.js';
import useSaveSouscriptionCustomerGranteesMutation from './useSaveSouscriptionCustomerGranteesMutation.js';
import { formatExtraDataBeforeSubmit } from '../extra/formatExtraData.js';
import { parseDate } from '@@viewHelpers/validations/index.js';

const BECLM_BLOCK_ERROR_PREFIX = 'BECLM_BLACKLIST_';
const BECLM_FAILED_ERROR = 'BECLM_BLACKLIST_FAILED';
const BECLM_MODAL_TEXT_KO = 'PROCESSDEVENTE_RETOUR_BECLM_KO_LABEL'; // FAILED (MATCH / STRUCK_OFF)
const BECLM_MODAL_TEXT_PENDING = 'PROCESSDEVENTE_RETOUR_BECLM_EN_ATTENTE_LABEL'; // BLOCKED (POTENTIAL_RISK / UNKNOWN)

const useSouscriptionCustomerGranteesForm = ({ project: data, middleFeatures, form, grantees }) => {
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({
    display: false,
    message: ''
  });
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  const initialBeclmBlocked = Boolean(prop('beclmBlocked', data));
  const [beclmBlocked, setBeclmBlocked] = useState(initialBeclmBlocked);

  const [beclmModalTextKey, setBeclmModalTextKey] = useState(
    initialBeclmBlocked ? BECLM_MODAL_TEXT_KO : null
  );
  const { processHandlers, context } = useProcess();

  useEffect(() => {
    if (initialBeclmBlocked) {
      console.log('[BECLM] Definitive block - view frozen (SUBSCRIPTION_FAILED)');
    }
  }, [initialBeclmBlocked]);

  const saveDetails = useSaveSouscriptionCustomerGranteesMutation();

  const minBirthDateReducChild = dateTsToSystem(prop('minBirthDateReducChild', data));
  const maxBirthDateReducChild = dateTsToSystem(prop('maxBirthDateReducChild', data));
  const minBirthDateHospiChild = dateTsToSystem(prop('minBirthDateHospiChild', data));
  const maxBirthDateHospiChild = dateTsToSystem(prop('maxBirthDateHospiChild', data));
  const offerCode = prop('offerCode', data);

  useEffect(() => {
    setIsUnauthorized(false);
  }, [
    form.watch('customer.firstName'),
    form.watch('customer.lastName'),
    form.watch('customer.birthDate')
  ]);

  const { isValid } = form.formState;

  const formatBirthDateToString = modify('birthDate', (birthDate) =>
    dateTimeToMidday(parseDate(birthDate)).valueOf().toString()
  );

  const onSubmit = (values) => {
    if (loading) return;
    setLoading(true);

    values = compose(
      dissoc('jobs'),
      evolve({
        grantees: map(
          compose(formatBirthDateToString, dissocPath(['attachedSocialSecurities', 0, 'referent']))
        ),
        customer: formatBirthDateToString
      }),
      dissoc('activePolicy'),
      when(pathEq(['customer', 'isMadelin'], false), dissocPath(['customer', 'madelinMonth'])),
      omit([
        'offerCode',
        'minBirthDateReducChild',
        'maxBirthDateReducChild',
        'minBirthDateHospiChild',
        'maxBirthDateHospiChild',
        'defaultGroupCode',
        'groupCodes',
        'beclmBlocked'
      ]),
      formatExtraDataBeforeSubmit
    )(values);

    saveDetails.onFormSubmit(values, (ok, error) => {
      if (ok && !error) {
        processHandlers.onNext({
          context: {
            projectId: context?.projectId
          }
        });
      } else {
        if (startsWith(BECLM_BLOCK_ERROR_PREFIX, defaultTo('', error))) {
          const isFailed = error === BECLM_FAILED_ERROR;
          console.log(
            isFailed
              ? '[BECLM] Definitive block - view frozen (SUBSCRIPTION_FAILED)'
              : `[BECLM] Flow blocked - view frozen until reload (${error})`
          );
          setBeclmBlocked(true);
          setBeclmModalTextKey(isFailed ? BECLM_MODAL_TEXT_KO : BECLM_MODAL_TEXT_PENDING);
        }
        setLoading(false);
      }
    });
  };

  const { ADHERENT_CODE_POSTAL_FILTRES_GEOGRAPHIQUES = [] } = middleFeatures;
  const postalCodeFranceMetro = getZipCodeRules(
    'France Métropolitaine',
    ADHERENT_CODE_POSTAL_FILTRES_GEOGRAPHIQUES
  );
  const postalCodeDROM = getZipCodeRules('DROM', ADHERENT_CODE_POSTAL_FILTRES_GEOGRAPHIQUES);

  const onPrevious = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  return {
    onSubmit,
    modal,
    setModal,
    isUnauthorized,
    postalCodeFranceMetro,
    postalCodeDROM,
    onPrevious,
    minBirthDateReducChild,
    maxBirthDateReducChild,
    minBirthDateHospiChild,
    maxBirthDateHospiChild,
    offerCode,
    grantees,
    isValid,
    beclmBlocked,
    beclmModalTextKey,
    closeBeclmModal: () => setBeclmModalTextKey(null)
  };
};

export default useSouscriptionCustomerGranteesForm;
